/**
 * Knowledge Base Page
 * Manage knowledge base articles and categories
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, Pencil, Trash2, BookOpen, FolderOpen, Eye } from 'lucide-react';
import { formatDateTime } from '@/lib/utils';

interface KbArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  categoryId: string | null;
  categoryName: string | null;
  applicationId: string | null;
  status: string;
  isPublic: boolean;
  tags: string | null;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

interface KbCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sortOrder: number;
  articleCount: number;
}

const statusColors: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-800',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

export function KnowledgeBasePage() {
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('articles');

  // Article filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [publicFilter, setPublicFilter] = useState<boolean | undefined>();
  const [page, setPage] = useState(1);

  // Article dialog state
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<KbArticle | null>(null);
  const [deleteArticleDialogOpen, setDeleteArticleDialogOpen] = useState(false);
  const [deletingArticle, setDeletingArticle] = useState<KbArticle | null>(null);

  // Article form state
  const [formTitle, setFormTitle] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formCategoryId, setFormCategoryId] = useState('');
  const [formApplicationId, setFormApplicationId] = useState('');
  const [formStatus, setFormStatus] = useState('draft');
  const [formIsPublic, setFormIsPublic] = useState(false);
  const [formTags, setFormTags] = useState('');

  // Category dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<KbCategory | null>(null);

  // Category form state
  const [catName, setCatName] = useState('');
  const [catSlug, setCatSlug] = useState('');
  const [catDescription, setCatDescription] = useState('');
  const [catSortOrder, setCatSortOrder] = useState('0');

  // Fetch articles
  const { data: articlesData, isLoading: articlesLoading } = useQuery({
    queryKey: ['kbArticles', { search, categoryId: categoryFilter, status: statusFilter, isPublic: publicFilter, page }],
    queryFn: async () => {
      const result = await api.getKbArticles({
        search: search || undefined,
        categoryId: categoryFilter,
        status: statusFilter,
        isPublic: publicFilter,
        page,
        limit: 20,
      });
      return result;
    },
  });

  const articles = (articlesData?.data || []) as KbArticle[];
  const pagination = articlesData?.pagination;

  // Fetch categories
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['kbCategories'],
    queryFn: async () => {
      const result = await api.getKbCategories();
      return result;
    },
  });

  const categories = (categoriesData?.data || []) as KbCategory[];

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ['kbArticles'] });
    queryClient.invalidateQueries({ queryKey: ['kbCategories'] });
  }

  // Article mutations
  const createArticleMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createKbArticle(data),
    onSuccess: () => {
      invalidateAll();
      closeArticleDialog();
    },
  });

  const updateArticleMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateKbArticle(id, data),
    onSuccess: () => {
      invalidateAll();
      closeArticleDialog();
    },
  });

  const deleteArticleMutation = useMutation({
    mutationFn: (id: string) => api.deleteKbArticle(id),
    onSuccess: () => {
      invalidateAll();
      setDeleteArticleDialogOpen(false);
      setDeletingArticle(null);
    },
  });

  // Category mutations
  const createCategoryMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createKbCategory(data),
    onSuccess: () => {
      invalidateAll();
      closeCategoryDialog();
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => api.deleteKbCategory(id),
    onSuccess: () => {
      invalidateAll();
      setDeleteCategoryDialogOpen(false);
      setDeletingCategory(null);
    },
  });

  // Article dialog functions
  function openCreateArticleDialog() {
    setEditingArticle(null);
    setFormTitle('');
    setFormSlug('');
    setFormContent('');
    setFormCategoryId('');
    setFormApplicationId('');
    setFormStatus('draft');
    setFormIsPublic(false);
    setFormTags('');
    setArticleDialogOpen(true);
  }

  function openEditArticleDialog(article: KbArticle) {
    setEditingArticle(article);
    setFormTitle(article.title);
    setFormSlug(article.slug);
    setFormContent(article.content);
    setFormCategoryId(article.categoryId || '');
    setFormApplicationId(article.applicationId || '');
    setFormStatus(article.status);
    setFormIsPublic(article.isPublic);
    setFormTags(article.tags || '');
    setArticleDialogOpen(true);
  }

  function openDeleteArticleDialog(article: KbArticle) {
    setDeletingArticle(article);
    setDeleteArticleDialogOpen(true);
  }

  function closeArticleDialog() {
    setArticleDialogOpen(false);
    setEditingArticle(null);
  }

  function handleArticleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const payload: Record<string, unknown> = {
      title: formTitle,
      slug: formSlug,
      content: formContent,
      categoryId: formCategoryId || null,
      applicationId: formApplicationId || null,
      status: formStatus,
      isPublic: formIsPublic,
      tags: formTags || null,
    };

    if (editingArticle) {
      updateArticleMutation.mutate({ id: editingArticle.id, data: payload });
    } else {
      createArticleMutation.mutate(payload);
    }
  }

  function handleTitleChange(value: string) {
    setFormTitle(value);
    if (!editingArticle) {
      setFormSlug(generateSlug(value));
    }
  }

  // Category dialog functions
  function openCreateCategoryDialog() {
    setCatName('');
    setCatSlug('');
    setCatDescription('');
    setCatSortOrder('0');
    setCategoryDialogOpen(true);
  }

  function closeCategoryDialog() {
    setCategoryDialogOpen(false);
  }

  function openDeleteCategoryDialog(category: KbCategory) {
    setDeletingCategory(category);
    setDeleteCategoryDialogOpen(true);
  }

  function handleCategorySubmit(e: React.FormEvent) {
    e.preventDefault();

    const payload: Record<string, unknown> = {
      name: catName,
      slug: catSlug,
      description: catDescription || null,
      sortOrder: parseInt(catSortOrder) || 0,
    };

    createCategoryMutation.mutate(payload);
  }

  function handleCatNameChange(value: string) {
    setCatName(value);
    setCatSlug(generateSlug(value));
  }

  const isArticleSaving = createArticleMutation.isPending || updateArticleMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Knowledge Base</h1>
          <p className="text-muted-foreground">Manage articles and categories</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="articles">Articles</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        {/* Articles Tab */}
        <TabsContent value="articles" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Knowledge Base Articles</h2>
            <Button onClick={openCreateArticleDialog}>
              <Plus className="h-4 w-4 mr-2" />
              New Article
            </Button>
          </div>

          {/* Article Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
            <Select
              value={categoryFilter || 'all'}
              onValueChange={(v) => {
                setCategoryFilter(v === 'all' ? undefined : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={statusFilter || 'all'}
              onValueChange={(v) => {
                setStatusFilter(v === 'all' ? undefined : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={publicFilter === true ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setPublicFilter(publicFilter === true ? undefined : true)}
            >
              <Eye className="h-4 w-4 mr-1" />
              Public Only
            </Button>
          </div>

          {/* Articles Table */}
          {articlesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : articles.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No articles found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {search || categoryFilter || statusFilter || publicFilter
                    ? 'Try adjusting your search or filters'
                    : 'Create your first knowledge base article'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Views</TableHead>
                      <TableHead className="text-center">Public</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {articles.map((article) => (
                      <TableRow
                        key={article.id}
                        className="cursor-pointer"
                        onClick={() => openEditArticleDialog(article)}
                      >
                        <TableCell className="font-medium">{article.title}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {article.categoryName || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[article.status]}`}
                          >
                            {statusLabels[article.status] || article.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-center text-sm">{article.viewCount}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={article.isPublic ? 'default' : 'secondary'}>
                            {article.isPublic ? 'Public' : 'Internal'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateTime(article.createdAt)}
                        </TableCell>
                        <TableCell>
                          <div
                            className="flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditArticleDialog(article)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteArticleDialog(article)}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Article Categories</h2>
            <Button onClick={openCreateCategoryDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>

          {categoriesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : categories.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">No categories yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create categories to organize your knowledge base articles
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-center">Articles</TableHead>
                    <TableHead className="text-center">Sort Order</TableHead>
                    <TableHead className="w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell className="font-medium">{category.name}</TableCell>
                      <TableCell className="text-sm">
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {category.slug}
                        </code>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {category.description || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{category.articleCount}</Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">{category.sortOrder}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteCategoryDialog(category)}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Article Create/Edit Dialog */}
      <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
        <DialogContent key={editingArticle?.id || 'new'} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? 'Edit Article' : 'New Article'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleArticleSubmit} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formTitle}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="e.g. How to reset your password"
                required
              />
            </div>

            <div>
              <Label>Slug *</Label>
              <Input
                value={formSlug}
                onChange={(e) => setFormSlug(e.target.value)}
                placeholder="e.g. how-to-reset-password"
                required
              />
            </div>

            <div>
              <Label>Content *</Label>
              <Textarea
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                placeholder="Write your article content here..."
                rows={12}
                className="w-full"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={formCategoryId || 'none'}
                  onValueChange={(v) => setFormCategoryId(v === 'none' ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Application</Label>
                <Input
                  value={formApplicationId}
                  onChange={(e) => setFormApplicationId(e.target.value)}
                  placeholder="Application ID (optional)"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select value={formStatus} onValueChange={setFormStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tags</Label>
                <Input
                  value={formTags}
                  onChange={(e) => setFormTags(e.target.value)}
                  placeholder="tag1, tag2, tag3"
                />
                <p className="text-xs text-muted-foreground mt-1">Comma-separated</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="public-checkbox"
                checked={formIsPublic}
                onChange={(e) => setFormIsPublic(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="public-checkbox" className="mb-0 cursor-pointer">
                Public (visible to clients)
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeArticleDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isArticleSaving || !formTitle || !formSlug || !formContent}
              >
                {isArticleSaving
                  ? 'Saving...'
                  : editingArticle
                    ? 'Update Article'
                    : 'Create Article'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Article Confirmation Dialog */}
      <Dialog open={deleteArticleDialogOpen} onOpenChange={setDeleteArticleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Article</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deletingArticle?.title}</strong>? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteArticleDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingArticle && deleteArticleMutation.mutate(deletingArticle.id)}
              disabled={deleteArticleMutation.isPending}
            >
              {deleteArticleMutation.isPending ? 'Deleting...' : 'Delete Article'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Create Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={catName}
                onChange={(e) => handleCatNameChange(e.target.value)}
                placeholder="e.g. Getting Started"
                required
              />
            </div>
            <div>
              <Label>Slug *</Label>
              <Input
                value={catSlug}
                onChange={(e) => setCatSlug(e.target.value)}
                placeholder="e.g. getting-started"
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={catDescription}
                onChange={(e) => setCatDescription(e.target.value)}
                placeholder="Optional category description"
                rows={3}
              />
            </div>
            <div>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={catSortOrder}
                onChange={(e) => setCatSortOrder(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeCategoryDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createCategoryMutation.isPending || !catName || !catSlug}
              >
                {createCategoryMutation.isPending ? 'Creating...' : 'Create Category'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <Dialog open={deleteCategoryDialogOpen} onOpenChange={setDeleteCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deletingCategory?.name}</strong>? Articles in
              this category will become uncategorized. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                deletingCategory && deleteCategoryMutation.mutate(deletingCategory.id)
              }
              disabled={deleteCategoryMutation.isPending}
            >
              {deleteCategoryMutation.isPending ? 'Deleting...' : 'Delete Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
